// Where an agent session stands. The Agent page, its reporting tools and the
// thread's own badge all read it from here, so a thread never shows one
// outcome on the page and another when opened.

export type AgentOutcomeBucket = "resolved" | "pending" | "escalated"

// The sweep counts an answer the customer left quiet for this long.
export const QUIET_RESOLUTION_MS = 24 * 60 * 60 * 1000

const HANDLED_THREAD_STATES = new Set(["PROCESSED", "SNOOZED", "COMPLETED"])

type Instant = Date | number

const toMs = (at: Instant) => (typeof at === "number" ? at : at.getTime())

export interface AgentOutcomeInput {
	isResolved: boolean | null | undefined
	escalatedAt: Instant | null | undefined
	threadState: string | null | undefined
	// Scoped to this session: see handedOffInSession.
	hasHandoffActivity: boolean
}

// Another session's handoff on the same thread is not this session's outcome.
// Rows written before the payload carried a session count for the whole thread.
export const handedOffInSession = (
	sessionId: string,
	activities: readonly { payload: unknown }[],
): boolean =>
	activities.some((activity) => {
		const owner = (activity.payload as { sessionId?: unknown } | null)
			?.sessionId
		return typeof owner !== "string" || owner === sessionId
	})

// A handoff is the outcome for good, even after the thread is closed. A bare
// escalatedAt only means a teammate replied, so it cannot undo a resolution.
export const classifyAgentOutcome = ({
	isResolved,
	escalatedAt,
	threadState,
	hasHandoffActivity,
}: AgentOutcomeInput): AgentOutcomeBucket | null => {
	if (hasHandoffActivity) return "escalated"
	if (isResolved === true) return "resolved"
	if (escalatedAt != null) return "escalated"
	const handled = threadState != null && HANDLED_THREAD_STATES.has(threadState)
	if (!handled) return "pending"
	return null
}

export interface OutcomeActivity {
	eventType: string
	payload: unknown
	createdAt: Instant
}

export interface OutcomeReason {
	text: string
	// The judge's own words, when a judge decided it.
	detail: string | null
	at: Date | null
}

const payloadOf = (activity: OutcomeActivity) =>
	(activity.payload ?? {}) as {
		sessionId?: unknown
		reason?: unknown
		change?: unknown
	}

const latest = (
	activities: readonly OutcomeActivity[],
	match: (activity: OutcomeActivity) => boolean,
) =>
	activities
		.filter(match)
		.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))[0]

const toDate = (at: Instant | null | undefined) =>
	at == null ? null : new Date(toMs(at))

/** One line on why a session landed in its bucket, from the rows that put it there. */
export const explainAgentOutcome = ({
	bucket,
	sessionId,
	resolvedAt,
	escalatedAt,
	updatedAt,
	lastInboundMessageAt,
	activities,
}: {
	bucket: AgentOutcomeBucket | null
	sessionId: string
	resolvedAt: Instant | null | undefined
	escalatedAt: Instant | null | undefined
	updatedAt: Instant
	lastInboundMessageAt: Instant | null | undefined
	activities: readonly OutcomeActivity[]
}): OutcomeReason | null => {
	// A row without a session predates the payload carrying one and covers the thread.
	const ownSession = (activity: OutcomeActivity) => {
		const owner = payloadOf(activity).sessionId
		return typeof owner !== "string" || owner === sessionId
	}
	const resolutionChange = (change: string) =>
		latest(
			activities,
			(a) =>
				a.eventType === "ai_resolution_changed" &&
				ownSession(a) &&
				payloadOf(a).change === change,
		)
	const judgeDetail = (activity: OutcomeActivity | undefined) => {
		const reason = activity ? payloadOf(activity).reason : null
		return typeof reason === "string" && reason.trim() ? reason.trim() : null
	}

	if (bucket === "escalated") {
		const handoff = latest(
			activities,
			(a) => a.eventType === "ai_handoff" && ownSession(a),
		)
		if (handoff) {
			return {
				text:
					payloadOf(handoff).reason === "agent_decision"
						? "The agent handed it to the team"
						: "The customer asked for a person",
				detail: null,
				at: toDate(handoff.createdAt),
			}
		}
		const judged = resolutionChange("judged_escalated")
		if (judged) {
			return {
				text: "Judged as not resolved",
				detail: judgeDetail(judged),
				at: toDate(judged.createdAt),
			}
		}
		return { text: "A teammate replied", detail: null, at: toDate(escalatedAt) }
	}

	if (bucket === "resolved") {
		const judged = resolutionChange("judged_resolved")
		if (judged) {
			return {
				text: "Judged as resolved",
				detail: judgeDetail(judged),
				at: toDate(judged.createdAt),
			}
		}
		const assumed = resolutionChange("assumed_resolved")
		if (assumed) {
			return {
				text: "No reply for 24 hours after the agent's answer",
				detail: null,
				at: toDate(assumed.createdAt),
			}
		}
		return {
			text: "The agent marked it resolved",
			detail: null,
			at: toDate(resolvedAt),
		}
	}

	if (bucket === "pending") {
		if (
			lastInboundMessageAt != null &&
			toMs(lastInboundMessageAt) > toMs(updatedAt)
		) {
			return {
				text: "The customer wrote after the agent's answer",
				detail: null,
				at: toDate(lastInboundMessageAt),
			}
		}
		return {
			text: "Counts as resolved if the customer stays quiet",
			detail: null,
			at: new Date(toMs(updatedAt) + QUIET_RESOLUTION_MS),
		}
	}

	return null
}
